import {toast} from 'react-toastify'
import {downloadOrderPackingSlipAsync} from 'src/redux/actions/orderActions'
import type {AppDispatch} from 'src/redux/store'
import {
  countMissingShippingLabels,
  downloadConsolidatedOrderDocumentsFromItems,
  getOrderPackingSlipDownloadFilename,
  OrderPackingSlipDownloadType,
} from './orderPackingSlipUtils'

const LEGACY_SHIPPING_LABEL_PLATFORM_NAMES = new Set(['TEMU', 'AMAZONSELLER'])
const AMAZON_VENDOR_DOCUMENT_DOWNLOAD_PLATFORM_NAME = 'AMAZONVENDOR'

export const isDocumentDownloadPlatform = (platformName: string | null | undefined): boolean => {
  return Boolean(platformName?.trim())
}

export const isOrderEligibleForDocumentDownload = (order: unknown): boolean => {
  if (!order || typeof order !== 'object') {
    return false
  }

  const record = order as Record<string, unknown>
  const platform = record.platform

  if (!platform || typeof platform !== 'object') {
    return false
  }

  const platformName = (platform as Record<string, unknown>).name
  const orderId = Number(record.id)

  return isDocumentDownloadPlatform(typeof platformName === 'string' ? platformName : null) &&
    Number.isFinite(orderId)
}

export const isLegacyShippingLabelPlatform = (platformName: string | null | undefined): boolean => {
  const normalized = platformName?.toUpperCase()?.trim()
  return Boolean(normalized && LEGACY_SHIPPING_LABEL_PLATFORM_NAMES.has(normalized))
}

export const hasConnectedDocumentDownloadPlatform = (platforms: any[] = []): boolean =>
  platforms.some((platform) => platform?.isConnected === true)

export const hasConnectedLegacyShippingLabelPlatform = (platforms: any[] = []): boolean =>
  platforms.some(
    (platform) => isLegacyShippingLabelPlatform(platform?.name) && platform?.isConnected === true
  )

export const filterOrdersForDocumentDownload = (orders: any[]): any[] =>
  orders.filter(isOrderEligibleForDocumentDownload)

export const isAmazonVendorDocumentDownloadPlatform = (
  platformName: string | null | undefined
): boolean => {
  return platformName?.toUpperCase()?.trim() === AMAZON_VENDOR_DOCUMENT_DOWNLOAD_PLATFORM_NAME
}

export const isOrderEligibleForAmazonVendorDocumentDownload = (order: unknown): boolean => {
  if (!isOrderEligibleForDocumentDownload(order)) {
    return false
  }

  const record = order as Record<string, unknown>
  const platform = record.platform as Record<string, unknown> | undefined
  const platformName = platform?.name

  return isAmazonVendorDocumentDownloadPlatform(
    typeof platformName === 'string' ? platformName : null
  )
}

export const filterOrdersForAmazonVendorDocumentDownload = (orders: any[]): any[] =>
  orders.filter(isOrderEligibleForAmazonVendorDocumentDownload)

export const filterOrdersForDocumentDownloadByType = (
  orders: any[],
  type: OrderPackingSlipDownloadType
): any[] => {
  if (type === 'PACKING_SLIP') {
    return filterOrdersForDocumentDownload(orders)
  }

  return filterOrdersForAmazonVendorDocumentDownload(orders)
}

export const hasConnectedAmazonVendorDocumentDownloadPlatform = (platforms: any[] = []): boolean =>
  platforms.some(
    (platform) =>
      isAmazonVendorDocumentDownloadPlatform(platform?.name) && platform?.isConnected === true
  )

export const resolveShowOrderDocumentDownloadActions = (options: {
  searchParams: URLSearchParams
  connectedPlatforms: any[]
  selectedOrdersMap: Record<string, any>
  selectedPlatform: string
}): boolean => {
  const {searchParams, connectedPlatforms, selectedOrdersMap, selectedPlatform} = options

  if (!hasConnectedDocumentDownloadPlatform(connectedPlatforms)) {
    return false
  }

  if (searchParams.get('show_action') === 'true') {
    return true
  }

  const selectedOrders = Object.values(selectedOrdersMap)
  if (selectedOrders.length > 0) {
    return selectedOrders.some(isOrderEligibleForDocumentDownload)
  }

  if (selectedPlatform) {
    const filteredPlatform = connectedPlatforms.find(
      (platform) => String(platform?.id) === String(selectedPlatform)
    )
    return filteredPlatform?.isConnected === true
  }

  return true
}

/** Shipping label and packing slip + label bulk actions (Amazon Vendor only). */
export const resolveShowAmazonVendorLabelDocumentDownloadActions = (options: {
  searchParams: URLSearchParams
  connectedPlatforms: any[]
  selectedOrdersMap: Record<string, any>
  selectedPlatform: string
}): boolean => {
  const {searchParams, connectedPlatforms, selectedOrdersMap, selectedPlatform} = options

  if (!hasConnectedAmazonVendorDocumentDownloadPlatform(connectedPlatforms)) {
    return false
  }

  if (searchParams.get('show_action') === 'true') {
    return true
  }

  const selectedOrders = Object.values(selectedOrdersMap)
  if (selectedOrders.length > 0) {
    return selectedOrders.some(isOrderEligibleForAmazonVendorDocumentDownload)
  }

  if (selectedPlatform) {
    const filteredPlatform = connectedPlatforms.find(
      (platform) => String(platform?.id) === String(selectedPlatform)
    )
    return (
      isAmazonVendorDocumentDownloadPlatform(filteredPlatform?.name) &&
      filteredPlatform?.isConnected === true
    )
  }

  return true
}

export const resolveShowLegacyShippingLabelActions = (options: {
  showShippingLabelActions: boolean
  connectedPlatforms: any[]
  selectedOrdersMap: Record<string, any>
  selectedPlatform: string
}): boolean => {
  if (!options.showShippingLabelActions) {
    return false
  }

  const selectedOrders = Object.values(options.selectedOrdersMap)
  if (selectedOrders.length > 0) {
    return selectedOrders.some((order) => isLegacyShippingLabelPlatform(order?.platform?.name))
  }

  if (options.selectedPlatform) {
    const filteredPlatform = options.connectedPlatforms.find(
      (platform) => String(platform?.id) === String(options.selectedPlatform)
    )
    return isLegacyShippingLabelPlatform(filteredPlatform?.name)
  }

  return hasConnectedLegacyShippingLabelPlatform(options.connectedPlatforms)
}

type RunOrderDocumentDownloadOptions = {
  dispatch: AppDispatch
  orderIds: number[]
  type: OrderPackingSlipDownloadType
  clearAllSelections: () => void
  successToast: (message: string) => void
  warningToast: (message: string) => void
  errorToast: (message: string) => void
}

export const runOrderDocumentDownload = async ({
  dispatch,
  orderIds,
  type,
  clearAllSelections,
  successToast,
  warningToast,
  errorToast,
}: RunOrderDocumentDownloadOptions): Promise<void> => {
  if (!orderIds.length) {
    warningToast('Please select orders to download documents.')
    return
  }

  const loadingToast = toast.info('Downloading documents... please wait.', {autoClose: false})

  try {
    const response = await dispatch(
      downloadOrderPackingSlipAsync({
        order_ids: orderIds,
        type,
      })
    ).unwrap()

    if (!response.urls.length && !response.items.length) {
      toast.dismiss(loadingToast)
      if (type === 'SHIPPING_LABEL') {
        warningToast('No shipping labels found for the selected orders.')
      } else if (type === 'PACKING_SLIP') {
        warningToast('No packing slips found for the selected orders.')
      } else {
        warningToast('No documents found for the selected orders.')
      }
      return
    }

    const missingLabelCount =
      type === 'BOTH' ? countMissingShippingLabels(response.items) : 0
    const filename = getOrderPackingSlipDownloadFilename(type)
    const {success, failedUrlCount, urlCount} =
      await downloadConsolidatedOrderDocumentsFromItems(
        {items: response.items, urls: response.urls},
        filename
      )

    if (urlCount === 0) {
      toast.dismiss(loadingToast)
      warningToast('No downloadable document URLs were returned for the selected orders.')
      return
    }

    toast.dismiss(loadingToast)

    if (!success) {
      errorToast('Failed to download documents.')
      return
    }

    if (response.shippingLabelRequestMessage) {
      warningToast(response.shippingLabelRequestMessage)
    }

    if (missingLabelCount > 0) {
      warningToast(
        `${missingLabelCount} order(s) did not have shipping labels yet. Downloaded available documents.`
      )
    }

    if (failedUrlCount > 0) {
      warningToast(
        `${failedUrlCount} document(s) could not be merged and were downloaded separately or skipped.`
      )
    }

    successToast(
      response.message ||
        (urlCount > 1
          ? 'Consolidated document downloaded successfully.'
          : 'Document downloaded successfully.')
    )
    clearAllSelections()
  } catch (error: unknown) {
    toast.dismiss(loadingToast)
    const message =
      error instanceof Error ? error.message : 'Failed to download documents. Please try again.'
    errorToast(message)
  }
}
